import { useContext, useEffect, useMemo, useState } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import type { QueryDslQueryContainer } from '@elastic/elasticsearch/lib/api/types'

import type { SearchAbleSelectProps } from '~/components/atoms/SearchAbleSelect/SearchAbleSelect'
import SearchAbleSelect from '~/components/atoms/SearchAbleSelect/SearchAbleSelect'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import {
  CurrentArtworkProps,
  initialState,
} from '~/contexts/SelectedArtworkReducer'
import { useSettingsContext } from '~/contexts/settingsContext'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useSelectbox } from '~/services/query'
import { useDebounce } from '~/services/useDebounce'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import useFormAnalytics from '~/hooks/useFormAnalytics'
import { queryEsForNumberOfArtworks } from '~/services/queryEsForNumberOfArtworks'
import { PushInteractionType } from '~/utils/posthogEvents'

import { getLabelByValue } from '../utils/getLabelByValue'

export interface ArtistSelectProps extends SearchAbleSelectProps {
  nextOnClick?: () => void
  getArtwork?: (currentArtist: string) => any
  testID?: string
  searchInsightsEvent?: TSearchAnalystEventProps
  placeholder?: string
  defaultValue?: {
    value: string
    label: string
  }
  useEmptyPrompt?: boolean
  preMenuTitle?: string
  smallHeight?: boolean
  filters?: QueryDslQueryContainer[]
}

export default function ArtistSelect({ filters, ...props }: ArtistSelectProps) {
  const t = useTranslations()
  const { currentArtworkDispatch, currentArtworkState } = useContext(
    SelectedArtworkContext
  )
  const { allArtists } = useSettingsContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm)
  const {
    handleFieldBlurAnalytics,
    handleSearchableSelectionAnalytics,
    handleManualArtistAdd,
  } = useFormAnalytics()

  const { data } = useSelectbox(
    debouncedSearchTerm,
    EsIndexTypes.ARTIST,
    RecordTypes.ARTIST_SELECT_LIST,
    10000,
    filters
  )

  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const filteredOptions = useMemo(() => {
    return (data || [])
      .reduce((accumulator: any, value: any) => accumulator.concat(value), [])
      .sort((a: any, b: any) => {
        if (searchTerm) return true
        const nameA = a.label
        const nameB = b.label
        return nameA?.localeCompare(nameB)
      })
      .sort((a: any, b: any) => {
        if (searchTerm) return true
        // Sort the options by MAB artists first.
        const aInReference = allArtists?.some(
          (item: any) => item._id === a.value
        )
        const bInReference = allArtists?.some(
          (item: any) => item._id === b.value
        )

        if (aInReference && !bInReference) {
          return -1 // a comes before b
        } else if (!aInReference && bInReference) {
          return 1 // b comes before a
        } else {
          return 0 // leave the order as is
        }
      })
      .filter((item: any) => {
        return item.value !== undefined
      })
  }, [data])

  useEffect(() => {
    if (props.defaultValue) {
      handleChange(props.defaultValue?.label, props.defaultValue?.value)
    }
  }, [props.defaultValue])

  const handleSearchTerm = (value: string) => {
    setSearchTerm(value)

    if (value === '') {
      // If the search term is cleared, reset the current artist.
      setCurrentArt(initialState)
    }
  }

  const replaceArtworkList = (payload: any) =>
    currentArtworkDispatch({ type: CurrentArtworkProps.UPDATE_LIST, payload })

  const setCurrentArt = (payload: any) =>
    currentArtworkDispatch({
      type: CurrentArtworkProps.UPDATE_SELECTED_ARTWORK,
      payload,
    })

  const eventProps: TSearchAnalystEventProps = useMemo(() => {
    return {
      method:
        props.searchInsightsEvent?.method ||
        SearchAnalystMethods.convertedObjectIDs,
      name:
        props.searchInsightsEvent?.name ||
        SearchAnalystEventNames.valuationArtistSelected,
    }
  }, [props.searchInsightsEvent])

  const handleChange = async (label: string, value: string) => {
    if (value && props.onChange) {
      let currentArtist = value
      const artistSelected = Boolean(currentArtist !== '')

      const artistName =
        filteredOptions.find(
          (option: { value: string }) => option.value === currentArtist
        )?.label || label

      if (!artistSelected && currentArtworkState.artist.id.length > 0) {
        currentArtist = currentArtworkState.artist.id
      }

      if (props.onChange) {
        props.onChange(props.name, currentArtist, artistName)
      }

      replaceArtworkList({
        artist: {
          name: artistName,
          id: currentArtist,
        },
      })

      const numberOfArtworks = await queryEsForNumberOfArtworks(currentArtist)

      if (props.onChange) {
        props.onChange('artistWithNoArtworks', numberOfArtworks === 0)
      }

      // Only triggers if the selected artist is different from the previous one.
      if (currentArtist !== currentArtworkState.artist.id) {
        sendSearchInsightsEvent(eventProps.method, {
          eventName: eventProps.name,
          indexType: EsIndexTypes.ARTIST,
          filters: [`artistId:${value}`],
        })

        handleSearchableSelectionAnalytics(
          'Selected Artist',
          PushInteractionType.LeftInputField,
          FormTypes.valuation,
          getLabelByValue(filteredOptions, currentArtist)
        )
      }
    } else {
      // If no current artist,
      // it's because they have cleared the field so send an onChange with currentArtist value of undefined.
      props?.onChange?.(props.name, undefined)
      setCurrentArt(initialState)
      return
    }
  }

  const selectProps = {
    ...props,
    value: props.value || currentArtworkState?.artist.id,
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFieldBlurAnalytics('User Types Manually Into Artist Field', e)
  }

  return (
    <>
      <SearchAbleSelect
        {...selectProps}
        options={filteredOptions}
        onChange={(label: string, value: any) => {
          handleChange(label, value)
        }}
        onBlurHandler={onBlurHandler}
        inputChange={handleSearchTerm}
        placeholder={selectProps.placeholder}
        preMenuTitle={
          props?.preMenuTitle
            ? props.preMenuTitle
            : t('forms.fields.popularSearches')
        }
      />
    </>
  )
}
